import React, { useEffect, useRef, useState } from 'react';
import { Brain, RefreshCw, X } from 'lucide-react';
import { streamChat } from '../../lib/streamingChat';
import { useAuth } from '../../lib/auth';
import StreamingMarkdown from '../StreamingMarkdown';

/**
 * AIAnalysisPanel — Claude-powered market read on the current chart.
 * Streams tokens from /api/crypto/ai/stream. Refresh button re-runs
 * against the newest data. Ask box lets operator override the default
 * prompt.
 */
export default function AIAnalysisPanel({ symbol, timeframe }) {
  const { token } = useAuth();
  const [text, setText] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [q, setQ] = useState('');
  const cancelRef = useRef(null);

  const run = (question = '') => {
    if (streaming || !symbol) return;
    setText(''); setStreaming(true);
    const backend = process.env.REACT_APP_BACKEND_URL;
    const url = `${backend}/api/crypto/ai/stream`;
    // Use a hand-rolled fetch/reader for a custom POST body — the
    // shared streamChat helper is GET-based.
    const ctrl = new AbortController();
    cancelRef.current = () => ctrl.abort();
    (async () => {
      try {
        const r = await fetch(url, {
          method: 'POST',
          signal: ctrl.signal,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/event-stream',
          },
          body: JSON.stringify({ symbol, timeframe, question }),
        });
        if (!r.ok || !r.body) {
          setText(`// upstream error: HTTP ${r.status}`);
          setStreaming(false); return;
        }
        const reader = r.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const frames = buf.split(/\n\n/);
          buf = frames.pop() || '';
          for (const f of frames) {
            const lines = f.split('\n');
            const evt = lines.find(l => l.startsWith('event:'))?.slice(6).trim();
            const dat = lines.find(l => l.startsWith('data:'))?.slice(5).trim();
            if (!dat) continue;
            try {
              const j = JSON.parse(dat);
              if (evt === 'token') setText((t) => t + (j.text || ''));
              else if (evt === 'error') setText((t) => t + `\n\n_// upstream error: ${j.error}_`);
            } catch { /* noop */ }
          }
        }
      } catch (e) {
        if (e?.name !== 'AbortError') setText((t) => t + `\n\n_// stream error: ${e.message}_`);
      } finally {
        setStreaming(false);
        cancelRef.current = null;
      }
    })();
  };

  // Auto-run on symbol / timeframe change (debounced). Also wipe any
  // pending question + previous response so a new coin never sees a
  // stale answer from the previous chart.
  useEffect(() => {
    if (!symbol) return;
    // Kill any in-flight stream immediately + reset UI state.
    cancelRef.current?.();
    setText('');
    setQ('');
    setStreaming(false);
    const t = setTimeout(() => run(''), 300);
    return () => { clearTimeout(t); cancelRef.current?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe]);

  return (
    <div className="border border-[var(--border-default)] bg-[var(--bg-panel)] flex flex-col"
         data-testid="crypto-ai-panel">
      <div className="flex items-center justify-between border-b border-[var(--border-default)] px-2 py-1.5">
        <div className="text-[10px] tracking-[0.35em] uppercase text-cyan-sys flex items-center gap-1.5">
          <Brain size={11} /> // ai read
        </div>
        <div className="flex items-center gap-1">
          {streaming ? (
            <button onClick={() => cancelRef.current?.()}
                    className="text-[9px] tracking-widest uppercase text-sell hover:underline flex items-center gap-1"
                    data-testid="crypto-ai-stop">
              <X size={10} /> stop
            </button>
          ) : (
            <button onClick={() => run(q)}
                    className="text-[9px] tracking-widest uppercase text-cyan-sys hover:underline flex items-center gap-1"
                    data-testid="crypto-ai-refresh">
              <RefreshCw size={10} /> refresh
            </button>
          )}
        </div>
      </div>
      <div className="p-2 max-h-72 overflow-y-auto text-[11px] leading-relaxed text-[var(--txt-primary)] atde-md">
        <StreamingMarkdown text={text} streaming={streaming} />
      </div>
      <div className="flex items-center gap-1 border-t border-[var(--border-default)] p-1.5">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !streaming && q.trim()) run(q); }}
          placeholder="ask a specific question…"
          disabled={streaming}
          data-testid="crypto-ai-input"
          className="flex-1 bg-black border border-[var(--border-default)] focus:border-cyan-sys outline-none px-2 py-1 text-[11px] font-mono text-cyan-sys"
        />
        <button
          onClick={() => run(q)}
          disabled={streaming || !q.trim()}
          className="border border-cyan-sys text-cyan-sys px-2 py-1 text-[9px] tracking-widest hover:bg-cyan-sys hover:text-black disabled:opacity-40"
          data-testid="crypto-ai-ask"
        >
          ASK
        </button>
      </div>
    </div>
  );
}
